import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Card, CardContent } from 'components/ui/card';
import { geocodeLocation, saveManualPing, type GeocodeResponse } from '../api/shipments';

interface ManualPingModalProps {
  isOpen: boolean;
  onClose: () => void;
  cnNumber: string;
  onSuccess?: () => void;
}

export const ManualPingModal: React.FC<ManualPingModalProps> = ({ 
  isOpen, 
  onClose, 
  cnNumber, 
  onSuccess 
}) => {
  const [locationQuery, setLocationQuery] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState<GeocodeResponse | null>(null);
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    setLocationQuery('');
    setGeocodeResult(null);
    onClose();
  };

  const handleFindLocation = async () => {
    if (!locationQuery.trim()) return;

    setGeocoding(true);
    try {
      const result = await geocodeLocation(locationQuery.trim());
      setGeocodeResult(result);
      
      if (!result.success) {
        toast.error(result.message || 'Failed to find location', {
          style: {
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca'
          }
        });
      }
    } catch (error) {
      console.error('Error geocoding location:', error);
      toast.error('Failed to find location', {
        style: {
          background: '#fef2f2',
          color: '#dc2626',
          border: '1px solid #fecaca'
        }
      });
    } finally {
      setGeocoding(false);
    }
  };

  const handleSavePing = async () => {
    if (!geocodeResult || !geocodeResult.success) return;

    setSaving(true);
    try {
      const pingData = {
        cn_number: cnNumber,
        lat: geocodeResult.lat!,
        lon: geocodeResult.lon!,
        formatted_address: geocodeResult.formatted_address!,
        locality: geocodeResult.locality!,
        city: geocodeResult.city!,
        state: geocodeResult.state!,
        country_code: geocodeResult.country_code!
      };

      const result = await saveManualPing(pingData);
      
      if (result.success) {
        toast.success('Manual ping saved successfully', {
          style: {
            background: '#f0fdf4',
            color: '#15803d',
            border: '1px solid #bbf7d0'
          }
        });
        onSuccess?.();
        handleClose();
      } else {
        toast.error('Failed to save manual ping', {
          style: {
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca'
          }
        });
      }
    } catch (error) {
      console.error('Error saving manual ping:', error);
      toast.error('Failed to save manual ping', {
        style: {
          background: '#fef2f2',
          color: '#dc2626',
          border: '1px solid #fecaca'
        }
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Manual Ping</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="location-input" className="text-sm font-medium">
              Location
            </Label>
            <div className="flex gap-2 mt-1">
              <input
                id="location-input"
                type="text"
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                placeholder="Enter location (e.g., Chakan, Pune, Maharashtra)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFindLocation();
                  }
                }}
              />
              <Button
                onClick={handleFindLocation}
                disabled={!locationQuery.trim() || geocoding}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {geocoding ? 'Finding...' : 'Find Location'}
              </Button>
            </div>
          </div>

          {geocodeResult && geocodeResult.success && (
            <Card>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium text-green-700 mb-2">Location Found</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Coordinates:</span>
                      <div className="font-medium">{(geocodeResult.lat ?? 0).toFixed(4)}, {(geocodeResult.lon ?? 0).toFixed(4)}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">City:</span>
                      <div className="font-medium">{geocodeResult.city}</div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Address:</span>
                      <div className="font-medium">{geocodeResult.formatted_address}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">State:</span>
                      <div className="font-medium">{geocodeResult.state}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Country:</span>
                      <div className="font-medium">{geocodeResult.country_code}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePing}
              disabled={!geocodeResult?.success || saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? 'Saving...' : 'Save Ping'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
